import { Activity, AlertTriangle } from "@tamagui/lucide-icons";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
} from "@tanstack/react-table";
import {
  Sensor,
  SensorStatus,
  SensorType,
  useSensorAlerts,
  useSensorEvents,
  useSensors,
} from "common";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router";
import {
  YStack,
  XStack,
  Text,
  Spinner,
  Button,
  Card,
  H2,
  H3,
  ScrollView,
  View,
  Separator,
  useMedia,
} from "tamagui";

import { TableCards } from "../components/TableCards";
import { useSensorType } from "../hooks/useSensorType";
import { formatDateTime, formatDateTimeSeconds } from "../utils/formatDate";

const SensorEvents = () => {
  const { t } = useTranslation();
  const { translateSensorType } = useSensorType();
  const navigate = useNavigate();
  const { hubId, sensorId } = useParams<{ hubId: string; sensorId: string }>();
  const { md } = useMedia();
  // Packet column shown when the columns don't all fit and become pills.
  const [selectedKind, setSelectedKind] = useState<string | null>(null);
  const [readingsWidth, setReadingsWidth] = useState<number | null>(null);
  const [sensor, setSensor] = useState<Sensor | null>(null);
  const [_sensorLoading, setSensorLoading] = useState<boolean>(true);
  const [_sensorError, setSensorError] = useState<Error | null>(null);
  const { getSensorById } = useSensors();

  const { sensorEvents: events, loading: eventsLoading } = useSensorEvents(
    undefined,
    sensorId,
  );

  const {
    sensorAlerts,
    loading: alertsLoading,
    error: alertsError,
  } = useSensorAlerts(sensorId);

  const fetchSensor = async (sensorId: string) => {
    setSensorLoading(true);

    try {
      const sensor = await getSensorById(sensorId);
      if (sensor) {
        setSensor(sensor);
      } else {
        throw new Error("Sensor not found");
      }
    } catch (error) {
      setSensorError(
        error instanceof Error ? error : new Error("Failed to fetch sensor"),
      );
    } finally {
      setSensorLoading(false);
    }
  };

  const handleBackClick = useCallback(() => {
    navigate(`/base-stations/${hubId}/sensors`);
  }, [hubId, navigate]);

  const renderSensorDetails = () => {
    if (!sensor) return null;

    return (
      <Card padding="$4" marginBottom="$4">
        <XStack
          justifyContent="space-between"
          alignItems="center"
          marginBottom="$2"
        >
          <H2 size="$15" $xs={{ fontSize: "$12", lineHeight: "$12" }}>
            {sensor.name}
          </H2>
          <View
            paddingHorizontal="$2"
            paddingVertical="$1"
            borderRadius="$4"
            backgroundColor={
              sensor.status === SensorStatus.Active ||
              sensor.status === SensorStatus.Active.toLowerCase()
                ? "$statusGood"
                : "$statusWarning"
            }
          >
            <Text color="$black12" fontWeight="bold">
              {sensor.status}
            </Text>
          </View>
        </XStack>
        <XStack flexWrap="wrap" gap="$4">
          <YStack space="$1" flex={1} minWidth={200}>
            <Text color="$gray11">{t("sensors.serialNumber")}</Text>
            <Text fontWeight="bold">{sensor.serialNumber}</Text>
          </YStack>
          <YStack space="$1" flex={1} minWidth={200}>
            <Text color="$gray11">{t("sensors.typeColumn")}</Text>
            <Text fontWeight="bold">
              {translateSensorType(sensor.type as SensorType)}
            </Text>
          </YStack>
          <YStack space="$1" flex={1} minWidth={200}>
            <Text color="$gray11">{t("sensors.location")}</Text>
            <Text fontWeight="bold">
              {sensor.location || t("common.notSpecified")}
            </Text>
          </YStack>
          {sensor.lastReported && (
            <YStack space="$1" flex={1} minWidth={200}>
              <Text color="$gray11">{t("sensors.lastReported")}</Text>
              <Text fontWeight="bold">
                {formatDistanceToNow(new Date(sensor.lastReported * 1000), {
                  addSuffix: true,
                })}
              </Text>
            </YStack>
          )}
        </XStack>
      </Card>
    );
  };

  const parseJsonSafely = (value: any) => {
    if (!value) return {};
    if (typeof value === "object") return value;
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return {};
      }
    }
    return {};
  };

  const formatCell = (val: any, forceDecimals = false) => {
    if (val === undefined || val === null) return "-";
    if (typeof val === "object") return JSON.stringify(val);
    if (typeof val === "number") {
      return !forceDecimals && Number.isInteger(val)
        ? String(val)
        : val.toFixed(2);
    }
    return String(val);
  };

  // One column per packet a sensor sends, in reading order. CT events are
  // "CT"; DATA events go by their packet_type (air handler R/S/D, power
  // A/B/C); DATA without one, and BATTERY, are "D".
  const PACKET_COLUMNS: Record<string, string[]> = {
    [SensorType.HVAC_AIRHANDLER_MONITOR]: ["CT", "R", "S", "D"],
    [SensorType.HVAC_CONDENSER_MONITOR]: ["CT", "D"],
    [SensorType.LEAK_DETECTOR]: ["D"],
    [SensorType.WATER_QUALITY]: ["D"],
    [SensorType.AIR_QUALITY]: ["D"],
    // Power only ever sends A/B/C (tx_power_monitor_data, packet 1-3).
    [SensorType.POWER]: ["A", "B", "C"],
    [SensorType.POWER_ALT]: ["A", "B", "C"],
  };

  const PACKET_LABELS: Record<string, string> = {
    CT: "CT",
    R: "Return (R)",
    S: "Supply (S)",
    D: "Data (D)",
    A: "Packet A",
    B: "Packet B",
    C: "Packet C",
  };

  const BATTERY_MERGE_SEC = 5;
  // Keep in step with .packet-columns in index.css.
  const PACKET_COLUMN_MIN = 230;

  const packetKind = (eventType: string, packetType: unknown) => {
    if (eventType === "CT") return "CT";
    return typeof packetType === "string" && packetType ? packetType : "D";
  };

  // Header text per value key. `temperature` is already converted to °F by
  // useSensorEvents; thermocouples and BSEC comp temps stay °C.
  const COLUMN_LABELS: Record<string, string> = {
    temperature: "Temperature (°F)",
    humidity: "Humidity (%)",
    percentPressure: "Pressure (%)",
    flow: "Flow (ft/s)",
    ct1: "CT 1 (A)",
    ct2: "CT 2 (A)",
    tc1: "Thermocouple 1 (°C)",
    tc2: "Thermocouple 2 (°C)",
    ec: "Electrical Conductivity (µS/cm)",
    leak: "Leak",
    iaq: "IAQ",
    staticIaq: "Static IAQ",
    pm: "PM (mg/m³)",
    co2Equivalent: "CO₂ Equivalent (ppm)",
    breathVoc: "Breath VOC (ppm)",
    rawGasResistance: "Gas Resistance (kΩ)",
    compGas: "Compensated Gas (log₁₀ Ω)",
    compTemperature: "Compensated Temperature (°C)",
    compHumidity: "Compensated Humidity (%)",
    gasPercentage: "Gas (%)",
    calibrating: "Calibrating",
    stabilizationStatus: "Stabilized",
    runInStatus: "Run-in Done",
    iaqAccuracyBsec: "IAQ Accuracy",
  };

  const columnLabel = (key: string) => {
    if (key === "pressure") {
      // Air handler reports static pressure in inH2O; air quality barometric in Pa.
      return sensor?.type === SensorType.AIR_QUALITY
        ? "Pressure (hPa)"
        : "Pressure (inH2O)";
    }
    if (COLUMN_LABELS[key]) return COLUMN_LABELS[key];
    const m = key.match(/^(vt|main_ct|branch_ct)(\d+)$/);
    if (m) {
      const [, prefix, n] = m;
      if (prefix === "vt") return `V${n} (V)`;
      if (prefix === "main_ct") return `Main ${n} (A)`;
      return `Branch ${n} (A)`;
    }
    // snake_case / camelCase → Title Case
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const YES_NO_KEYS = new Set([
    "leak",
    "calibrating",
    "stabilizationStatus",
    "runInStatus",
  ]);

  const formatValue = (key: string, val: any, forceDecimals: boolean) => {
    if (YES_NO_KEYS.has(key) && (val === 0 || val === 1))
      return val ? "Yes" : "No";
    // Display scaling: AQ barometric Pa → hPa, raw MOX resistance Ω → kΩ.
    if (typeof val === "number") {
      if (key === "pressure" && sensor?.type === SensorType.AIR_QUALITY)
        return formatCell(val / 100, true);
      if (key === "rawGasResistance") return formatCell(val / 1000, true);
    }
    return formatCell(val, forceDecimals);
  };

  // Display order. Raw readings first, derived channels next, status flags,
  // battery last. Power keys sort ahead of everything (volts → mains → branches).
  const COLUMN_ORDER = [
    "temperature",
    "humidity",
    "pressure",
    "percentPressure",
    "flow",
    "tc1",
    "tc2",
    "ct1",
    "ct2",
    "leak",
    "ec",
    "iaq",
    "iaqAccuracyBsec",
    "staticIaq",
    "pm",
    "co2Equivalent",
    "breathVoc",
    "gasPercentage",
    "rawGasResistance",
    "compGas",
    "compTemperature",
    "compHumidity",
    "calibrating",
    "stabilizationStatus",
    "runInStatus",
    "battery",
  ];

  const columnRank = (key: string) => {
    if (key.startsWith("vt")) return -3;
    if (key.startsWith("main_ct")) return -2;
    if (key.startsWith("branch_ct")) return -1;
    const idx = COLUMN_ORDER.indexOf(key);
    return idx === -1 ? COLUMN_ORDER.length : idx;
  };

  const {
    tableData,
    sortedValueColumns,
    packetKinds,
    fractionalColumns,
    batteryUnit,
  } = useMemo(() => {
    const allRows = (events || []).map((event: any): Record<string, any> => {
      const { packet_type: packetType, ...values }: Record<string, any> = {
        ...parseJsonSafely(event.values),
      };
      // BATTERY events carry the level in `value`, not `values`.
      if (
        event.eventType === "BATTERY" &&
        values.battery === undefined &&
        event.value !== undefined
      ) {
        values.battery = Number(event.value);
      }
      return {
        timestamp: event.timestamp,
        kind: packetKind(event.eventType, packetType),
        _battery: event.eventType === "BATTERY",
        ...values,
      };
    });
    // A BATTERY packet rides with its DATA packet (same second on air
    // quality, leak, water): show it on that D card, not as a card of its
    // own. Leak DATA already carries battery, so a matching one is a repeat.
    const rows = allRows.filter((r: any) => {
      if (!r._battery) return true;
      const host = allRows.find(
        (d: any) =>
          d.kind === "D" &&
          !d._battery &&
          (d.battery === undefined || d.battery === r.battery) &&
          Math.abs(d.timestamp - r.timestamp) <= BATTERY_MERGE_SEC,
      );
      if (!host) return true;
      host.battery = r.battery;
      return false;
    });

    const valueColumns = new Set<string>();
    const seenKinds = new Set<string>();
    const fractional = new Set<string>();
    let maxBattery = 0;
    rows.forEach((r: any) => {
      seenKinds.add(r.kind);
      Object.keys(r).forEach((k) => {
        if (k === "timestamp" || k === "kind" || k.startsWith("_")) return;
        valueColumns.add(k);
        if (typeof r[k] === "number" && !Number.isInteger(r[k]))
          fractional.add(k);
      });
      if (typeof r.battery === "number")
        maxBattery = Math.max(maxBattery, r.battery);
    });
    // Known columns always show (even before their first packet); anything
    // unexpected gets its own column after them rather than vanishing.
    const known = PACKET_COLUMNS[sensor?.type ?? ""] ?? [];
    return {
      tableData: rows,
      sortedValueColumns: Array.from(valueColumns).sort(
        (a, b) =>
          columnRank(a) - columnRank(b) ||
          a.localeCompare(b, undefined, { numeric: true }),
      ),
      packetKinds: [
        ...known,
        ...Array.from(seenKinds).filter((k) => !known.includes(k)),
      ],
      fractionalColumns: fractional,
      batteryUnit: maxBattery > 100 ? "mV" : "%",
    };
  }, [events, sensor?.type]);

  const headerLabel = (col: string) =>
    col === "battery" ? `Battery (${batteryUnit})` : columnLabel(col);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<any>();
    return [
      columnHelper.accessor("timestamp", {
        header: () => t("sensors.timestamp", "Timestamp"),
        cell: (info) => formatDateTimeSeconds(info.getValue()),
      }),
      ...sortedValueColumns.map((col) =>
        columnHelper.accessor(col, {
          id: `val_${col}`,
          header: () => headerLabel(col),
          cell: (info) =>
            formatValue(col, info.getValue(), fractionalColumns.has(col)),
        }),
      ),
    ];
  }, [
    sortedValueColumns,
    fractionalColumns,
    batteryUnit,
    sensor?.type,
    t,
  ]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const renderSensorReadings = () => {
    const renderEventsTable = () => {
      if (!events || events.length === 0) {
        return (
          <YStack alignItems="center" padding="$4">
            <Activity size={32} color="$gray10" />
            <Text color="$gray10" marginTop="$2">
              {t("sensors.noReadingsYet", "No readings yet.")}
            </Text>
          </YStack>
        );
      }

      // A column per packet type, newest on top, each card listing only
      // what that packet carried. Side by side while every column gets its
      // 230px (index.css .packet-columns); narrower, pills pick one at a time.
      const rows = table.getRowModel().rows;
      const rowsOf = (kind: string) =>
        rows.filter((row) => row.original.kind === kind);
      const label = (kind: string) => PACKET_LABELS[kind] ?? `Packet ${kind}`;
      const columnsFit =
        readingsWidth === null
          ? !md
          : readingsWidth >= packetKinds.length * (PACKET_COLUMN_MIN + 14);
      const tabbed = packetKinds.length > 1 && !columnsFit;
      const activeKind =
        selectedKind && packetKinds.includes(selectedKind)
          ? selectedKind
          : packetKinds[0];
      const shownKinds = tabbed ? [activeKind] : packetKinds;

      return (
        <YStack
          gap="$3"
          onLayout={(e) => setReadingsWidth(e.nativeEvent.layout.width)}
        >
          {tabbed && (
            <XStack gap="$2" flexWrap="wrap">
              {packetKinds.map((kind) => (
                <Button
                  key={kind}
                  size="$3"
                  borderRadius="$14"
                  onPress={() => setSelectedKind(kind)}
                  borderWidth={kind === activeKind ? 2 : 0}
                  borderColor={kind === activeKind ? "$gray10" : "$transparent"}
                  {...{ "aria-pressed": kind === activeKind }}
                >
                  <Text fontSize="$3">{label(kind)}</Text>
                  <Text fontSize="$2" color="$gray10">
                    {rowsOf(kind).length}
                  </Text>
                </Button>
              ))}
            </XStack>
          )}
          <XStack
            className={shownKinds.length > 1 ? "packet-columns" : undefined}
            gap="$3"
            maxWidth={shownKinds.length * 460}
          >
            {shownKinds.map((kind) => {
              const kindRows = rowsOf(kind);
              return (
                <YStack key={kind} gap="$3" flex={1} minWidth={0}>
                  {!tabbed && (
                    <Text fontSize="$6" fontWeight="700">
                      {label(kind)}
                    </Text>
                  )}
                  {kindRows.length > 0 ? (
                    <TableCards
                      table={table}
                      rows={kindRows}
                      titleColumn="timestamp"
                      skipEmpty
                    />
                  ) : (
                    <Text color="$gray10">
                      {t("sensors.noPacketsYet", "No packets yet")}
                    </Text>
                  )}
                </YStack>
              );
            })}
          </XStack>
        </YStack>
      );
    };

    return (
      <YStack marginTop="$4" minWidth={0} maxWidth="100%">
        <H3
          size="$14"
          marginBottom="$2"
          $xs={{ fontSize: "$10", lineHeight: "$10" }}
        >
          {t("sensors.sensorReadings", "Sensor Readings")}
        </H3>
        <Card
          padding="$4"
          minWidth={0}
          maxWidth="100%"
          overflow="hidden"
          $xs={{ padding: "$3" }}
        >
          {eventsLoading ? (
            <YStack alignItems="center" padding="$4">
              <Spinner size="large" />
              <Text marginTop="$2">{t("common.loading", "Loading...")}</Text>
            </YStack>
          ) : (
            <YStack minWidth={0} maxWidth="100%">
              <Text fontSize="$2" color="$gray11" marginBottom="$2">
                {t(
                  "sensors.realTimeUpdates",
                  "Real-time updates enabled. New readings will appear automatically.",
                )}
              </Text>
              {renderEventsTable()}
            </YStack>
          )}
        </Card>
      </YStack>
    );
  };

  const renderSensorAlerts = () => {
    const renderAlertsContent = () => {
      if (alertsLoading) {
        return (
          <YStack alignItems="center" padding="$4">
            <Spinner size="large" />
            <Text marginTop="$2">{t("common.loading", "Loading...")}</Text>
          </YStack>
        );
      }

      if (alertsError) {
        return (
          <YStack alignItems="center" padding="$4">
            <AlertTriangle size={32} color="$red10" />
            <Text color="$red10" marginTop="$2">
              {alertsError.message ||
                t("sensors.failedToLoadAlerts", "Failed to load alerts.")}
            </Text>
          </YStack>
        );
      }

      if (!sensorAlerts || sensorAlerts.length === 0) {
        return (
          <YStack alignItems="center" padding="$4">
            <AlertTriangle size={32} color="$gray10" />
            <Text color="$gray10" marginTop="$2">
              {t("sensors.noAlertsYet", "No alerts yet.")}
            </Text>
          </YStack>
        );
      }

      return (
        <YStack>
          <Text fontSize="$2" color="$gray11" marginBottom="$2">
            {t(
              "sensors.realTimeAlerts",
              "Real-time alerts enabled. New alerts will appear automatically.",
            )}
          </Text>
          <ScrollView>
            {sensorAlerts.map((alert) => (
              <Card
                key={alert.id + alert.timestamp}
                padding="$3"
                marginBottom="$3"
                bordered
              >
                <XStack
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap="$2"
                >
                  <Text fontWeight="bold">
                    {alert.alertType} ({alert.severity})
                  </Text>
                  <Text color="$gray11" fontSize="$2">
                    {formatDateTime(alert.timestamp)}
                  </Text>
                </XStack>
                <Separator marginVertical="$2" />
                <Text fontWeight="bold" marginBottom="$1">
                  {alert.message}
                </Text>
                {alert.value !== undefined && (
                  <Text color="$gray11" fontSize="$2" marginBottom="$1">
                    Value:{" "}
                    {typeof alert.value === "object"
                      ? JSON.stringify(alert.value, null, 2)
                      : String(alert.value)}
                  </Text>
                )}
                {alert.parsedMetadata &&
                  Object.keys(alert.parsedMetadata).length > 0 && (
                    <YStack marginTop="$1">
                      <Text color="$gray11" fontSize="$2" marginBottom="$1">
                        Metadata:
                      </Text>
                      <XStack flexWrap="wrap" gap="$2">
                        {Object.entries(alert.parsedMetadata).map(
                          ([key, value]) => (
                            <View
                              key={key}
                              paddingHorizontal="$2"
                              paddingVertical="$1"
                              borderRadius="$4"
                              backgroundColor="$blue5"
                            >
                              <Text color="$blue11" fontSize="$2">
                                {key}: {String(value)}
                              </Text>
                            </View>
                          ),
                        )}
                      </XStack>
                    </YStack>
                  )}
                <XStack marginTop="$2" gap="$4" rowGap="$1" flexWrap="wrap">
                  <Text color="$gray10" fontSize="$2">
                    Status: {alert.status}
                  </Text>
                  {alert.acknowledgedAt && (
                    <Text color="$gray10" fontSize="$2">
                      Ack: {formatDateTime(alert.acknowledgedAt)}
                    </Text>
                  )}
                  {alert.acknowledgedBy && (
                    <Text color="$gray10" fontSize="$2">
                      By: {alert.acknowledgedBy}
                    </Text>
                  )}
                </XStack>
              </Card>
            ))}
          </ScrollView>
        </YStack>
      );
    };

    return (
      <YStack marginTop="$6">
        <H3
          size="$14"
          marginBottom="$2"
          $xs={{ fontSize: "$10", lineHeight: "$10" }}
        >
          {t("sensors.alerts", "Sensor Alerts")}
        </H3>
        <Card padding="$4" $xs={{ padding: "$3" }}>
          {renderAlertsContent()}
        </Card>
      </YStack>
    );
  };

  useEffect(() => {
    if (!sensorId) {
      return;
    }

    fetchSensor(sensorId);
  }, [sensorId]);

  return (
    <YStack
      flex={1}
      padding="$4"
      space
      minWidth={0}
      maxWidth="100%"
      $xs={{ paddingHorizontal: 0 }}
    >
      <XStack alignItems="center" space="$2" marginBottom="$4">
        <Button
          size="$3"
          chromeless
          onPress={handleBackClick}
          aria-label={t("a11y.back")}
          icon={<Text fontSize="$10">←</Text>}
        />
        <Text fontSize="$9" fontWeight="bold">
          {t("sensors.sensorReadings")}
        </Text>
      </XStack>
      {renderSensorDetails()}
      {renderSensorReadings()}
      {renderSensorAlerts()}
    </YStack>
  );
};

export default SensorEvents;
